import { BaseService } from './BaseService';
import { ApiResponse } from '@/types';
import { PartidoInfo } from '@/types/partido';
import { Categoria } from '@/types/enums';

/**
 * Servicio para gestionar información de partidos obtenida del web scraping
 */
class PartidoInfoService extends BaseService {
  constructor() {
    super('/partido-info');
  }

  /**
   * Obtiene el próximo partido disponible
   * @returns Información del próximo partido
   */
  async obtenerProximoPartido(): Promise<ApiResponse<PartidoInfo>> {
    try {
      return await this.get<PartidoInfo>('/proximo');
    } catch (error) {
      console.error('Error al obtener próximo partido:', error);
      throw error;
    }
  }

  /**
   * Obtiene partidos por categoría
   * @param categoria - Categoría del equipo
   * @returns Lista de partidos de la categoría
   */
  async obtenerPartidosPorCategoria(categoria: Categoria): Promise<ApiResponse<PartidoInfo[]>> {
    try {
      return await this.get<PartidoInfo[]>(`/categoria/${categoria}`);
    } catch (error) {
      console.error('Error al obtener partidos por categoría:', error);
      throw error;
    }
  }

  /**
   * Obtiene todos los partidos disponibles
   * @returns Lista de todos los partidos
   */
  async obtenerTodosLosPartidos(): Promise<ApiResponse<PartidoInfo[]>> {
    try {
      return await this.getAll<PartidoInfo>();
    } catch (error) {
      console.error('Error al obtener todos los partidos:', error);
      throw error;
    }
  }

  /**
   * TEMPORAL: Ejecuta web scraping manualmente para testing
   * @returns Resultado de la operación
   */
  async ejecutarScrapingTest(): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}/partido-info/ejecutar-scraping-test`, {
        method: 'POST',
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error ejecutando scraping test:', error);
      throw error;
    }
  }
  
  /**
   * TEMPORAL: Obtiene información de diagnóstico para testing
   * @returns Información de diagnóstico
   */
  async obtenerDiagnosticoTest(): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}/partido-info/diagnostico-test`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error obteniendo diagnóstico:', error);
      throw error;
    }
  }

  /**
   * Obtiene el partido del equipo asignado al entrenador
   * @param entrenadorId ID del entrenador
   * @returns Información del partido del equipo del entrenador
   */
  async obtenerPartidoPorEntrenador(entrenadorId: number): Promise<ApiResponse<PartidoInfo>> {
    try {
      return await this.get<PartidoInfo>(`/entrenador/${entrenadorId}`);
    } catch (error) {
      console.error('Error al obtener partido por entrenador:', error);
      throw error;
    }
  }
}

export const partidoInfoService = new PartidoInfoService();